import { computed, ref } from 'vue';
import Helper from '../libs/Helper';

const UTM_COOKIE_NAME = 'slct_utm_params';
const UTM_COOKIE_MAX_AGE = 60 * 60 * 24; // 24 hours
const CONSENT_EVENT = 'consent_status';
const CONSENT_WINDOW_EVENT = 'UC_SDK_EVENT';

export const UTM_FIELDS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid'
] as const;

export type UtmField = typeof UTM_FIELDS[number];
export type UtmParams = Partial<Record<UtmField, string>>;

const currentParams = ref<UtmParams>({});
const hasConsent = ref(false);
let initialized = false;

/** Values to render as hidden form fields. Only fields that have a value. */
export const utmParams = computed(() => currentParams.value);

function pickUtmParams(params: URLSearchParams): UtmParams {
    return UTM_FIELDS.reduce((obj: UtmParams, field) => {
        const value = (params.get(field) || '').trim();
        if (value) {
            obj[field] = value;
        }
        return obj;
    }, {});
}

function readCookie(): UtmParams {
    try {
        const cookie = Helper.getCookie(UTM_COOKIE_NAME);
        return cookie ? pickUtmParams(new URLSearchParams(cookie)) : {};
    } catch (e) {
        return {};
    }
}

function writeCookie(params: UtmParams) {
    const value = encodeURIComponent(new URLSearchParams(params as Record<string, string>).toString());
    document.cookie = `${UTM_COOKIE_NAME}=${value};max-age=${UTM_COOKIE_MAX_AGE};path=/;SameSite=Lax`;
}

function deleteCookie() {
    document.cookie = `${UTM_COOKIE_NAME}=;max-age=0;path=/;SameSite=Lax`;
}

function isConsentEvent(entry: any): boolean {
    return !!entry && entry.event === CONSENT_EVENT && !!entry.ucCategory;
}

function applyConsent(entry: any) {
    hasConsent.value = entry.ucCategory.marketing === true;
    if (hasConsent.value) {
        persist();
    } else {
        deleteCookie();
    }
}

function persist() {
    if (hasConsent.value && Object.keys(currentParams.value).length) {
        writeCookie(currentParams.value);
    }
}

function watchConsent() {
    // consent that was already reported before this script ran
    const latest = (window.dataLayer || []).filter(isConsentEvent).pop();
    if (latest) {
        applyConsent(latest);
    }
    // Usercentrics dispatches the same payload as window event on every later consent change
    window.addEventListener(CONSENT_WINDOW_EVENT, (e: CustomEvent) => {
        if (isConsentEvent(e.detail)) {
            applyConsent(e.detail);
        }
    });
}

/**
 * Reads UTM parameters and gclid from the current URL, falls back to the cookie of an earlier page view,
 * and persists them in a first-party cookie for 24h if marketing consent was granted.
 * Call once on page load, so landing pages without forms are tracked as well.
 */
export function initUtmTracking() {
    if (initialized) {
        return;
    }
    initialized = true;

    // params in the URL replace the stored ones as a whole, so values of different campaigns are never mixed
    const urlParams = pickUtmParams(new URLSearchParams(location.search));
    currentParams.value = Object.keys(urlParams).length ? urlParams : readCookie();
    watchConsent();
}
